import { useCallback, useEffect, useRef, useState } from "react";

const TICK = 100;
const SILENCE_END_MS = 1300;
const IDLE_STOP_MS = 20000;
const MAX_UTTER_MS = 30000;

// Hands-free conversation loop: listen → (silence) send → speak → listen again. Barge-in while speaking.
export function useVoiceLoop({ onUtterance }) {
  const [mode, setModeState] = useState("off"); // off | listening | thinking | speaking
  const r = useRef({ mode: "off" }).current;
  const setMode = (m) => { r.mode = m; setModeState(m); };

  const level = () => {
    if (!r.analyser) return 0;
    r.analyser.getFloatTimeDomainData(r.buf);
    let s = 0;
    for (let i = 0; i < r.buf.length; i++) s += r.buf[i] * r.buf[i];
    return Math.sqrt(s / r.buf.length);
  };

  const stopRec = (send) => {
    r.send = send;
    if (r.rec && r.rec.state === "recording") r.rec.stop();
  };

  const listen = useCallback(() => {
    if (!r.active || !r.stream) return;
    r.chunks = [];
    r.had = false; r.speech = 0; r.silence = 0; r.idle = 0; r.total = 0; r.send = false;
    const rec = new MediaRecorder(r.stream);
    rec.ondataavailable = (e) => e.data.size && r.chunks.push(e.data);
    rec.onstop = () => {
      if (!r.send) return;
      const blob = new Blob(r.chunks, { type: rec.mimeType || "audio/webm" });
      onUtterance(blob, rec.mimeType || "");
    };
    r.rec = rec;
    rec.start();
    setMode("listening");
  }, [onUtterance]); // eslint-disable-line react-hooks/exhaustive-deps

  const tick = () => {
    const lvl = level();
    if (r.mode === "listening") {
      r.total += TICK;
      if (r.total <= 500) r.floor = Math.max(r.floor || 0, lvl);
      const th = Math.max(0.015, (r.floor || 0) * 2.5);
      if (lvl > th) { r.speech += TICK; r.silence = 0; if (r.speech >= 200) r.had = true; }
      else if (r.had) r.silence += TICK;
      else r.idle += TICK;
      if (r.had && (r.silence >= SILENCE_END_MS || r.total >= MAX_UTTER_MS)) { stopRec(true); setMode("thinking"); }
      else if (!r.had && r.idle >= IDLE_STOP_MS) stop();
    } else if (r.mode === "speaking") {
      const th = Math.max(0.05, (r.floor || 0) * 6);
      r.barge = lvl > th ? (r.barge || 0) + TICK : 0;
      if (r.barge >= 300) { r.barge = 0; r.audio?.pause(); listen(); }
    }
  };

  const start = async () => {
    try {
      r.stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true } });
    } catch (_e) { return false; }
    const Ctx = window.AudioContext || window.webkitAudioContext;
    r.ctx = new Ctx();
    r.analyser = r.ctx.createAnalyser();
    r.analyser.fftSize = 1024;
    r.buf = new Float32Array(r.analyser.fftSize);
    r.ctx.createMediaStreamSource(r.stream).connect(r.analyser);
    r.floor = 0;
    r.active = true;
    r.timer = setInterval(tick, TICK);
    listen();
    return true;
  };

  const stop = () => {
    r.active = false;
    clearInterval(r.timer);
    stopRec(false);
    r.audio?.pause();
    r.stream?.getTracks().forEach((t) => t.stop());
    r.ctx?.close?.();
    r.stream = null; r.analyser = null;
    setMode("off");
  };

  // Play an answer. In conversation mode, listening resumes when it ends.
  const speak = (url) => {
    r.audio?.pause();
    const a = new Audio(url);
    r.audio = a;
    if (r.active) setMode("speaking");
    a.onended = () => { if (r.active && r.mode === "speaking") listen(); };
    a.play().catch(() => { if (r.active) listen(); });
  };
  const resume = () => { if (r.active) listen(); };
  const stopAudio = () => r.audio?.pause();

  useEffect(() => () => stop(), []); // eslint-disable-line react-hooks/exhaustive-deps

  return { mode, active: mode !== "off", start, stop, speak, resume, stopAudio, setThinking: () => setMode(r.active ? "thinking" : "off") };
}
